import { Plugs } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { type McpInfo, mcpInfoQuery, spacesQuery } from "@/components/admin/api";
import { QueryBoundary } from "@/components/admin/shared";
import { ClaudeConnect } from "@/components/settings/ClaudeConnect";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { Badge, CopyField, type DataColumn, DataTable, FormSection, RowSkeleton, Select } from "@/components/ui";
import snippetDoc from "../../../../../docs/claude-snippet.md?raw";

export const Route = createFileRoute("/_app/settings/claude")({ component: ClaudePage });

/** The fenced markdown block of docs/claude-snippet.md: the single source of truth for the snippet. */
const SNIPPET = (/````markdown\r?\n([\s\S]*?)\r?\n````/.exec(snippetDoc)?.[1] ?? "").replace(/\r\n/g, "\n");

function ClaudePage() {
  useBreadcrumbs([{ label: m.nav_settings(), to: "/settings" }, { label: m.set_nav_claude() }]);
  const info = useQuery(mcpInfoQuery);
  return (
    <Page title={m.set_nav_claude()} icon={Plugs} meta={m.mcp_meta()} width="full" className="max-w-5xl">
      <QueryBoundary query={info} skeleton={<RowSkeleton rows={4} />}>
        {(data) => (
          <div className="flex flex-col">
            <FormSection title={m.mcp_connect_title()} description={m.mcp_connect_hint()} wide>
              <ClaudeConnect info={data} />
            </FormSection>
            <FormSection title={m.mcp_snippet_title()} description={m.mcp_snippet_hint()} wide>
              <Snippet />
            </FormSection>
            <FormSection title={m.mcp_tools_title()} description={m.mcp_tools_hint({ count: data.tools.length })} wide>
              <Tools tools={data.tools} />
            </FormSection>
          </div>
        )}
      </QueryBoundary>
    </Page>
  );
}

function Snippet() {
  const spaces = useQuery(spacesQuery);
  const products = (spaces.data ?? []).flatMap((s) =>
    s.products.filter((p) => !p.isSpaceProduct).map((p) => ({ ...p, space: s.name })),
  );
  const [key, setKey] = useState<string | null>(null);
  const chosen = key ?? products[0]?.key ?? "PLZ";
  const text = useMemo(() => SNIPPET.replaceAll("PLZ", chosen), [chosen]);
  return (
    <div className="flex flex-col gap-3">
      {products.length > 0 ? (
        <Select
          label={m.mcp_snippet_product()}
          description={m.mcp_snippet_product_hint()}
          className="max-w-72"
          items={products.map((p) => ({ id: p.key, label: `${p.key}, ${p.name}` }))}
          selectedKey={chosen}
          onSelectionChange={setKey}
        />
      ) : null}
      <CopyField label={m.mcp_snippet_label()} value={text} multiline rows={16} />
    </div>
  );
}

function Tools({ tools }: { tools: McpInfo["tools"] }) {
  type Row = McpInfo["tools"][number] & { id: string };
  const rows: Row[] = tools.map((t) => ({ ...t, id: t.name }));
  const columns: DataColumn<Row>[] = [
    {
      id: "tool",
      header: m.mcp_tools_col_tool(),
      isRowHeader: true,
      cell: (t) => (
        <div className="flex flex-col">
          <span className="font-medium text-fg">{t.title}</span>
          <span className="font-mono text-xs text-fg-muted">{t.name}</span>
        </div>
      ),
    },
    {
      id: "access",
      header: m.mcp_tools_col_access(),
      className: "w-36",
      cell: (t) =>
        t.readOnly ? <Badge>{m.mcp_tools_read()}</Badge> : <Badge tone="accent">{m.mcp_tools_write()}</Badge>,
    },
  ];
  return <DataTable label={m.mcp_tools_title()} columns={columns} rows={rows} />;
}
